import axios from "axios";
import { Api } from "../Api";
import { initAudioRecordingsDB } from "./db";
import { AUDIO_RECORDINGS_DB_STORE } from "./db";
import { AUDIO_RECORDINGS_DB_AUDIO_BLOB } from "./db";
import { AUDIO_RECORDINGS_DB_SIGNATURE_BLOB } from "./db";
import { v4 as uuidv4 } from "uuid";

const MP4_MIME_TYPE = "audio/mp4";
const WAV_MIME_TYPE = "audio/wav";

const guessContentType = (blob) => {
  const t = blob?.type;
  if (typeof t === "string" && t.trim().length > 0) return t;
  return MP4_MIME_TYPE;
};

const uploadPart = async (
  upload_url,
  partBlob,
  partNumber,
  totalParts,
  setUploadProgress
) => {
  console.log("uploadPart ", upload_url, partBlob, partNumber, totalParts);
  try {
    const response = await axios.put(upload_url, partBlob, {
      headers: {
        "Content-Type": "", // Leave empty unless explicitly required in pre-signed URL
      },
      onUploadProgress: (progressEvent) => {
        const partProgress = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        const overallProgress = Math.round(
          ((partNumber - 1 + partProgress / 100) / totalParts) * 100
        );
        setUploadProgress(overallProgress); // Update progress bar incrementally
      },
    });

    return response;
  } catch (error) {
    console.error(
      `Error uploading part ${partNumber}:`,
      error.response || error.message
    );
    throw error;
  }
};

/**
 * Multipart upload a provided Blob/File using the existing S3 presign flow.
 * Supports per-part retry and resuming an incomplete upload (re-uploads only missing parts).
 *
 * Returns:
 * - { status: "completed", uuid, filePath, bucketName }
 * - { status: "paused", resumeState, errorMessage }
 * - { status: "failed", errorMessage }
 */
const uploadBlobMultipart = async ({
  uploadType,
  objectId,
  blob,
  fileName,
  setUploadProgress,
  toastFn,
  contentTypeOverride,
  resumeState,
  onResumeStateChange,
  maxRetriesPerPart = 3,
}) => {
  if (!blob) {
    toastFn?.({
      title: "Error",
      description: "No audio file to upload",
      status: "error",
      duration: 5000,
      isClosable: true,
    });
    return { status: "failed", errorMessage: "No audio file to upload" };
  }

  const controller = new AbortController();
  const signal = controller.signal;

  const chunkSize = 5 * 1024 * 1024; // 5MB chunk size
  const numParts = Math.ceil(blob.size / chunkSize);

  const object_uuid = resumeState?.uuid || (objectId ? objectId : uuidv4().toString());
  const content_type = resumeState?.content_type || contentTypeOverride || guessContentType(blob);

  let upload_id = resumeState?.upload_id;
  let file_path = resumeState?.file_path;
  let bucket_name = resumeState?.bucket_name;
  let parts = Array.isArray(resumeState?.parts) ? [...resumeState.parts] : [];

  const emitResumeState = (next) => {
    try {
      onResumeStateChange?.(next);
    } catch {
      // ignore
    }
  };

  const indexParts = () => {
    const map = new Map();
    parts.forEach((p) => {
      if (p && typeof p.part_number === "number") map.set(p.part_number, p);
    });
    return map;
  };

  try {
    setUploadProgress?.(Math.round((Math.min(parts.length, numParts) / Math.max(1, numParts)) * 100));

    if (!upload_id || !file_path) {
      const initiateResponse = await Api.post(
        "/initiate-multipart-upload",
        // Prefer new backend contract; include legacy fields for backwards compatibility.
        {
          file_name: fileName || `upload_${object_uuid}`,
          content_type,
          type: uploadType,
          uuid: object_uuid,
        },
        { signal }
      );
      upload_id = initiateResponse.data.upload_id;
      file_path = initiateResponse.data.file_path;
      bucket_name = initiateResponse.data.bucket_name;
      // New backend contract uses upload_uuid (same concept as our uuid).
      const upload_uuid = initiateResponse.data.upload_uuid;
      if (upload_uuid) {
        // Treat upload_uuid as canonical when available.
        // (Keep storing in `uuid` field name for resumeState shape.)
        // eslint-disable-next-line no-unused-vars
        const _ = upload_uuid;
      }
      parts = [];
    }

    for (let partNumber = 1; partNumber <= numParts; partNumber++) {
      const partsByNumber = indexParts();
      if (partsByNumber.has(partNumber)) {
        // Already uploaded; skip.
        setUploadProgress?.(Math.round((Math.min(partsByNumber.size, numParts) / Math.max(1, numParts)) * 100));
        continue;
      }

      const start = (partNumber - 1) * chunkSize;
      const end = Math.min(partNumber * chunkSize, blob.size);
      const partBlob = blob.slice(start, end);

      const presignedUrlResponse = await Api.get(
        // Prefer new contract: upload_uuid + url field. Include legacy params for compat.
        `/get-part-upload-url?upload_id=${encodeURIComponent(
          upload_id
        )}&upload_uuid=${encodeURIComponent(object_uuid)}&part_number=${partNumber}&type=${encodeURIComponent(
          uploadType
        )}&uuid=${encodeURIComponent(object_uuid)}`,
        { signal }
      );

      const upload_url =
        presignedUrlResponse.data?.url || presignedUrlResponse.data?.upload_url;

      let lastErr = null;
      for (let attempt = 1; attempt <= maxRetriesPerPart; attempt++) {
        try {
          const uploadResponse = await uploadPart(
            upload_url,
            partBlob,
            partNumber,
            numParts,
            setUploadProgress || (() => {})
          );
          if (uploadResponse.status !== 200) {
            throw new Error(`HTTP ${uploadResponse.status}`);
          }
          const etag =
            uploadResponse?.headers?.etag ||
            uploadResponse?.headers?.ETag ||
            uploadResponse?.headers?.get?.("etag") ||
            uploadResponse?.headers?.get?.("ETag");
          parts.push({ etag, part_number: partNumber });

          emitResumeState({
            uploadType,
            uuid: object_uuid,
            upload_id,
            file_path,
            bucket_name,
            content_type,
            parts,
            num_parts: numParts,
          });
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          if (attempt === maxRetriesPerPart) break;
        }
      }

      if (lastErr) {
        const paused = {
          uploadType,
          uuid: object_uuid,
          upload_id,
          file_path,
          bucket_name,
          content_type,
          parts,
          num_parts: numParts,
        };
        emitResumeState(paused);
        return {
          status: "paused",
          resumeState: paused,
          errorMessage: `Failed to upload part ${partNumber}`,
        };
      }
    }

    const completeResponse = await Api.post(
      "/complete-multipart-upload",
      // Prefer new backend contract; include legacy fields for backwards compatibility.
      {
        upload_id,
        upload_uuid: object_uuid,
        parts,
        type: uploadType,
        file_path,
      },
      { signal }
    );

    const finalFilePath = completeResponse.data?.file_path || file_path;
    const finalBucketName = completeResponse.data?.bucket_name || bucket_name;
    emitResumeState(null);
    return {
      status: "completed",
      uuid: object_uuid,
      filePath: finalFilePath,
      bucketName: finalBucketName,
    };
  } catch (error) {
    if (error.name === "AbortError") {
      return { status: "failed", errorMessage: "Upload aborted" };
    }
    toastFn?.({
      title: "Error",
      description: `Error uploading audio: ${error.message}`,
      status: "error",
      duration: 5000,
      isClosable: true,
    });
    return { status: "failed", errorMessage: error.message };
  } finally {
    window.addEventListener("beforeunload", () => controller.abort());
  }
};

const uploadAudio = async (
  uploadType,
  objectId,
  onUploadComplete,
  setUploadProgress,
  toastFn
) => {
  const db = await initAudioRecordingsDB();
  let blob = null;
  if (uploadType === "conversation") {
    blob = await db.get(
      AUDIO_RECORDINGS_DB_STORE,
      AUDIO_RECORDINGS_DB_AUDIO_BLOB
    );
  } else if (uploadType === "signature") {
    blob = await db.get(
      AUDIO_RECORDINGS_DB_STORE,
      AUDIO_RECORDINGS_DB_SIGNATURE_BLOB
    );
  }
  console.log("uploadAudio ", blob);
  if (blob) {
    // Create an AbortController instance
    const controller = new AbortController();
    const signal = controller.signal;
    const object_uuid = objectId ? objectId : uuidv4().toString();

    try {
      setUploadProgress(0); // Reset progress

      // Initiate the multipart upload. Use the blob's real MIME type (signature
      // clips are normalized to WAV; recordings are mp4/webm) and fall back to
      // mp4 only if the blob has no type.
      const initiateResponse = await Api.post(
        "/initiate-multipart-upload",
        {
          type: uploadType,
          uuid: object_uuid,
          content_type: blob.type || MP4_MIME_TYPE,
        },
        {
          signal,
        }
      );

      const { upload_id, file_path } = initiateResponse.data;
      const chunkSize = 5 * 1024 * 1024; // 5MB chunk size
      const numParts = Math.ceil(blob.size / chunkSize);
      let parts = [];

      for (let partNumber = 1; partNumber <= numParts; partNumber++) {
        const start = (partNumber - 1) * chunkSize;
        const end = Math.min(partNumber * chunkSize, blob.size);
        const partBlob = blob.slice(start, end);
        const presignedUrlResponse = await Api.get(
          `/get-part-upload-url?type=${encodeURIComponent(
            uploadType
          )}&upload_id=${upload_id}&part_number=${partNumber}&uuid=${encodeURIComponent(
            object_uuid
          )}`,
          {
            signal,
          }
        );

        const { upload_url } = presignedUrlResponse.data;
        console.log("upload_url ", upload_url);
        const uploadResponse = await uploadPart(
          upload_url,
          partBlob,
          partNumber,
          numParts,
          setUploadProgress
        );

        if (uploadResponse.status !== 200) {
          throw new Error(`Failed to upload part ${partNumber}`);
        }

        const etag = uploadResponse.headers.get("etag");
        parts.push({ etag, part_number: partNumber });
      }

      // Complete the multipart upload
      const completeResponse = await Api.post(
        "/complete-multipart-upload",
        {
          type: uploadType,
          file_path,
          upload_id,
          parts,
        },
        {
          signal,
        }
      );

      const { file_path: finalFilePath, bucket_name } = completeResponse.data;
      console.log(`Audio uploaded successfully: ${finalFilePath}`);
      if (onUploadComplete) {
        onUploadComplete(object_uuid, finalFilePath, bucket_name);
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Upload aborted");
      } else {
        toastFn({
          title: "Error",
          description: `Error uploading audio: ${error.message}`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }

    window.addEventListener("beforeunload", () => controller.abort());
  } else {
    toastFn({
      title: "Error",
      description: "No audio file to upload",
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  }
};

export { uploadAudio, uploadBlobMultipart, MP4_MIME_TYPE, WAV_MIME_TYPE };
